import { USER_DATA, USER_EDIT, USER_START, USER_END, USER_CREATE, USER_UPDATE, USER_DELETE } from "../type";
import { validateToken, redirectToLogin } from '../../../utils/auth/tokenValidation';

interface Action {
  type: string;
  payload?: any; 
  loading?: boolean;
}

type Dispatch = (action: Action) => void;


const API_URL = process.env.REACT_APP_API_URL


export const getAllUser = (params: { page?: number;  size?: number; sort?: string[];}) => async (dispatch: Dispatch): Promise<void> => {
  
  if (!validateToken()) {
    redirectToLogin();
    return;
  }
  
  try {
    dispatch({ type: USER_START, payload: true });

    const token = localStorage.getItem("loginToken");
    if (!token) throw new Error("No auth token found");

    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.set("page", params.page.toString());
    }
    if (params.size !== undefined) {
      queryParams.set("size", params.size.toString());
    }
  

    const url = `${API_URL}/api/users?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const res = await response.json();

    if (response.ok) {
      dispatch({
        type: USER_DATA,
        payload: res.data,          
      });
    } else {
      dispatch({
        type: USER_END,
        payload: res.message || "Failed to load users",
      });
    }
  } catch (error: any) {
    dispatch({
      type: USER_END,
      payload: error.message || "Network error",
    });
    console.error("Get users failed:", error);
  }
};








export const createUser =
  (payload: any) => async (dispatch: Dispatch): Promise<any> => {
    
    if (!validateToken()) {
      redirectToLogin();
      return { success: false, message: "Authentication required" };
    }
    
    try {
      dispatch({
        type: USER_START,
        loading: true,
      });

      const token = localStorage.getItem("loginToken");
      if (!token) {
        throw new Error("Authorization token not found");
      }

      const response = await fetch(
        `${API_URL}/api/users`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const res = await response.json();

      if (response.ok) {
        dispatch({
          type: USER_CREATE,
          payload: res,
          loading: false,
        });
      return { success: true, data: res };
      } else {
        dispatch({
          type: USER_END,
          payload: res,
          loading: false,
        });
        return { success: false, message: res.exceptionMsg || res.message };
      }
    } catch (error:any) {
      dispatch({
        type: USER_END,
        payload: error,
        loading: false,
      });
      return { success: false, message: error.message };
    }
  };




  export const getById =
  (id: number) => async (dispatch: Dispatch): Promise<any> => {
    
    if (!validateToken()) {
      redirectToLogin();
      return { success: false, message: "Authentication required" };
    }
    
    try {
      dispatch({ type: USER_START, loading: true });

      const token = localStorage.getItem("loginToken");
      if (!token) throw new Error("Authorization token not found");

      const response = await fetch(
        `${API_URL}/api/users/${id}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const res = await response.json();

      if (response.ok) {
        dispatch({
          type: USER_EDIT,
          payload: res,
          loading: false,
        });
        return { success: true, data: res };
      } else {
        dispatch({
          type: USER_END,
          payload: res,
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      dispatch({
        type: USER_END,
        payload: error.message,
        loading: false,
      });
      return { success: false };
    }
  };




export const UpdateUser =
  (id:Number, payload: any) => async (dispatch: Dispatch): Promise<any> => {
    
    if (!validateToken()) {
      redirectToLogin();
      return { success: false, message: "Authentication required" };
    }
    
    try {
      dispatch({ type: USER_START, loading: true });

      const token = localStorage.getItem("loginToken");
      if (!token) throw new Error("Authorization token not found");

      const response = await fetch(
        `${API_URL}/api/users/${id}`,
        {
          method: "PUT", 
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const res = await response.json();

      if (response.ok) {
        dispatch({
          type: USER_UPDATE,
          payload: res,
          loading: false,
        });
        return { success: true, data: res };
      } else {
        dispatch({
          type: USER_END,
          payload: res,
          loading: false,
        });
        return { success: false, message: res.exceptionMsg || res.message };
      }
    } catch (error: any) {
      dispatch({
        type: USER_END,
        payload: error.message,
        loading: false,
      });
      return { success: false, message: error.message };
    }
  };

export const unlockUser = (id: number) => async (dispatch: Dispatch): Promise<any> => {
  if (!validateToken()) {
    redirectToLogin();
    return { success: false, message: "Authentication required" };
  }
  
  try {
    dispatch({ type: USER_START, loading: true });

    const token = localStorage.getItem("loginToken");
    if (!token) throw new Error("Authorization token not found");

    const response = await fetch(`${API_URL}/api/users/${id}/unlock`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const text = await response.text();
    const res = text ? JSON.parse(text) : {};

    if (response.ok) {
      dispatch({ type: USER_UPDATE, payload: res, loading: false });
      return { success: true, message: res.message || "User unlocked successfully" };
    } else {
      dispatch({ type: USER_END, payload: res, loading: false });
      return { success: false, message: res.message || "Failed to unlock user" };
    }
  } catch (error: any) {
    dispatch({ type: USER_END, payload: error.message, loading: false });
    return { success: false, message: error.message };
  }
};

export const resetPassword = (id: number, newPassword: string) => async (dispatch: Dispatch): Promise<any> => {
  if (!validateToken()) {
    redirectToLogin();
    return { success: false, message: "Authentication required" };
  }
  
  try {
    dispatch({ type: USER_START, loading: true });

    const token = localStorage.getItem("loginToken");
    if (!token) throw new Error("Authorization token not found");

    const response = await fetch(`${API_URL}/api/users/${id}/reset/password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ newPassword }),
    });

    const text = await response.text();
    const res = text ? JSON.parse(text) : {};

    if (response.ok) {
      dispatch({ type: USER_UPDATE, payload: res, loading: false });
      return { success: true, message: res.message || "Password reset successfully" };
    } else {
      dispatch({ type: USER_END, payload: res, loading: false });
      return { success: false, message: res.exceptionMsg || res.message || "Failed to reset password" };
    }
  } catch (error: any) {
    dispatch({ type: USER_END, payload: error.message, loading: false });
    return { success: false, message: error.message };
  }
};








// new actions for approval and rejection of users
export const approveUser = (id: number) => async (dispatch: Dispatch): Promise<any> => {
  if (!validateToken()) {
    redirectToLogin();
    return { success: false, message: "Authentication required" };
  }

  try {
    dispatch({ type: USER_START, loading: true });

    const token = localStorage.getItem("loginToken");
    if (!token) throw new Error("Authorization token not found");

    const response = await fetch(`${API_URL}/api/users/${id}/approve`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const text = await response.text();
    const res = text ? JSON.parse(text) : {};

    if (response.ok) {
      dispatch({ type: USER_UPDATE, payload: res, loading: false });
      return { success: true, message: res.message || "User approved successfully" };
    } else {
      dispatch({ type: USER_END, payload: res, loading: false });
      return { success: false, message: res.message || "Failed to approve user" };
    }
  } catch (error: any) {
    dispatch({ type: USER_END, payload: error.message, loading: false });
    return { success: false, message: error.message };
  }
};








export const deleteUser = (id: number) => async (dispatch: Dispatch): Promise<any> => {
  if (!validateToken()) {
    redirectToLogin();
    return { success: false, message: "Authentication required" };
  }

  try {
    dispatch({ type: USER_START, loading: true });

    const token = localStorage.getItem("loginToken");
    if (!token) throw new Error("Authorization token not found");

    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const text = await response.text();
    const res = text ? JSON.parse(text) : {};

    if (response.ok) {
      dispatch({ type: USER_DELETE, payload: id, loading: false });
      return { success: true, message: res.message || "User deleted successfully" };
    } else {
      dispatch({ type: USER_END, payload: res, loading: false });
      return { success: false, message: res.message || "Failed to delete user" };
    }
  } catch (error: any) {
    dispatch({ type: USER_END, payload: error.message, loading: false });
    return { success: false, message: error.message };
  }
};

export const rejectUser = (id: number) => async (dispatch: Dispatch): Promise<any> => {
  if (!validateToken()) {
    redirectToLogin();
    return { success: false, message: "Authentication required" };
  }

  try {
    dispatch({ type: USER_START, loading: true });

    const token = localStorage.getItem("loginToken");
    if (!token) throw new Error("Authorization token not found");

    const response = await fetch(`${API_URL}/api/users/${id}/reject`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const text = await response.text();
    const res = text ? JSON.parse(text) : {};

    if (response.ok) {
      dispatch({ type: USER_UPDATE, payload: res, loading: false });
      return { success: true, message: res.message || "User rejected successfully" };
    } else {
      dispatch({ type: USER_END, payload: res, loading: false });
      return { success: false, message: res.message || "Failed to reject user" };
    }
  } catch (error: any) {
    dispatch({ type: USER_END, payload: error.message, loading: false });
    return { success: false, message: error.message };
  }
};



